import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type {
  LanguageDto,
  SelfDeleteRequest,
  SelfDto,
  SelfPrivacySettingsRequest,
  SelfUpdateRequest,
  UserAccountNotificationSettingsRequest,
} from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { toUiLanguageId } from "@/helpers/languages";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useGeneralQueries } from "@/queries/general/queryOptions";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";
import type { ApiResponseType } from "@/types/api-types";

const useUpdateSelf = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const sessionUser = useSessionUser();

  return useMutation({
    mutationFn: (payload: SelfUpdateRequest) => api.putSelfV3(payload),
    onSuccess(_, payload) {
      // Refresh content across projects, but keep public bootstrap data and SDK identity.
      if (
        payload.autoTranslationLanguageId !== sessionUser.autoTranslationLanguage?.id ||
        payload.uiLanguageId !== sessionUser.uiLanguage.id
      ) {
        void queryClient.invalidateQueries({
          predicate: ({ queryKey: [root] }) =>
            root !== "application-config" &&
            root !== "session-credential-identity" &&
            root !== "LANGUAGES" &&
            root !== "TRANSLATIONS" &&
            root !== "CONFIGURATIONS",
        });
      } else {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF(projectId) });
      }
    },
  });
};

const useUpdateNotificationSettings = () => {
  const queryClient = useQueryClient();
  const api = useApi();

  return useMutation({
    mutationFn: (payload: UserAccountNotificationSettingsRequest) => api.patchAccountNotificationSettingsV1(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF_ACCOUNT_NOTIFICATION_SETTINGS() });
    },
  });
};

const useDeleteSelf = () => {
  const api = useApi();

  return useMutation({
    mutationFn: (payload: SelfDeleteRequest) => api.postSelfDeleteV1(payload),
  });
};

const useUpdatePrivacySettings = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: SelfPrivacySettingsRequest) => api.patchSelfPrivacySettingsV1(payload),
    onSuccess: async () => {
      // The privacy modal remounts on open and seeds its toggles from SELF; reopening early would show old settings.
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF(projectId) });
    },
  });
};

const useMarkOnboarded = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: () => api.postSelfOnboardingV1(),
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.onboarding.help-categories.submit.error"),
      });
    },
    onSuccess: async () => {
      // Callers navigate on success; the route shell redirects back to onboarding while SELF has no onboardedAt.
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF(projectId) });
    },
  });
};

const useGetRegistrationCode = () => {
  const api = useApi();

  return useMutation({
    mutationFn: () => api.getSelfAddressV1().then((response) => response.data.code),
  });
};

const useUpdateLanguage = () => {
  const projectId = useProjectId();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const api = useApi();
  const showFlashToast = useFlashToast();
  const generalQueries = useGeneralQueries();

  return useMutation({
    mutationFn: (payload: { uiLanguageId: LanguageDto["id"]; autoTranslationLanguageId?: LanguageDto["id"] }) =>
      api
        .putSelfLanguageV1({
          uiLanguageId: toUiLanguageId(payload.uiLanguageId),
          autoTranslationLanguageId: payload.autoTranslationLanguageId,
        })
        .then(({ data }) => data),
    onMutate: async (payload) => {
      const uiLanguages = await queryClient.query({
        ...generalQueries.languages({ subset: "ui" }),
        staleTime: "static",
      });
      const autoTranslationLanguages = await queryClient.query({
        ...generalQueries.languages({ subset: "autoTranslation" }),
        staleTime: "static",
      });

      const newUiLanguage = uiLanguages.find((language) => language.id === payload.uiLanguageId);

      if (newUiLanguage) {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({
          queryKey: QUERY_KEYS.SELF(projectId),
        });

        // Snapshot the previous value
        const previousSelfQueryData = queryClient.getQueryData<ApiResponseType<"getSelfV2"> | undefined>(
          QUERY_KEYS.SELF(projectId),
        );

        // Optimistically update the cache with the new language
        queryClient.setQueryData<ApiResponseType<"getSelfV2"> | undefined>(
          QUERY_KEYS.SELF(projectId),
          (oldResponse) => {
            if (oldResponse) {
              const newData: SelfDto = {
                ...oldResponse.data,
                language: newUiLanguage,
                uiLanguage: newUiLanguage,
              };

              if (payload.autoTranslationLanguageId) {
                newData.autoTranslationLanguage = autoTranslationLanguages?.find(
                  (lang) => lang.id === payload.autoTranslationLanguageId,
                );
              }

              return {
                ...oldResponse,
                data: newData,
              };
            }
          },
        );

        if (previousSelfQueryData) {
          const selfUserId = previousSelfQueryData.data.id;

          const previousQueryData = queryClient.getQueryData<ApiResponseType<"getUsersDetailsV1"> | undefined>(
            QUERY_KEYS.USER_DETAILS(projectId, selfUserId),
          );
          queryClient.setQueryData<ApiResponseType<"getUsersDetailsV1"> | undefined>(
            QUERY_KEYS.USER_DETAILS(projectId, selfUserId),
            (oldResponse) => {
              if (oldResponse) {
                return {
                  ...oldResponse,
                  data: {
                    ...oldResponse.data,
                    uiLanguageId: toUiLanguageId(newUiLanguage.id),
                    autoTranslationLanguageId: payload.autoTranslationLanguageId,
                  },
                };
              }
            },
          );

          return {
            previousQueryData: previousQueryData,
            previousSelfQueryData: previousSelfQueryData,
          };
        }

        return {
          previousSelfQueryData: previousSelfQueryData,
        };
      }
    },
    onSuccess: () => {
      // Refresh content across projects, but keep public bootstrap data and SDK identity.
      void queryClient.invalidateQueries({
        predicate: ({ queryKey: [root] }) =>
          root !== "application-config" &&
          root !== "session-credential-identity" &&
          root !== "LANGUAGES" &&
          root !== "TRANSLATIONS" &&
          root !== "CONFIGURATIONS",
      });
    },
    onError: (_, __, context) => {
      showFlashToast({
        variant: "alert",
        message: t("model.self.action.change-language.notification.error"),
      });

      if (context?.previousSelfQueryData) {
        queryClient.setQueryData(QUERY_KEYS.SELF(projectId), context.previousSelfQueryData);
      }

      if (context?.previousQueryData) {
        const selfUserId = context.previousSelfQueryData.data.id;
        queryClient.setQueryData(QUERY_KEYS.USER_DETAILS(projectId, selfUserId), context.previousQueryData);
      }
    },
  });
};

export const selfAccountMutations = {
  useUpdateSelf,
  useUpdateNotificationSettings,
  useDeleteSelf,
  useUpdatePrivacySettings,
  useMarkOnboarded,
  useGetRegistrationCode,
  useUpdateLanguage,
};
